import React from 'react';
import { FileUp, X } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const POEUploadModal = ({ isOpen, onClose, onSubmit, assessments = [], initialAssessmentId = '' }) => {
  const [assessmentId, setAssessmentId] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [note, setNote] = React.useState('');
  const [error, setError] = React.useState('');
  const [uploading, setUploading] = React.useState(false);

  React.useEffect(() => {
    if (!isOpen) return;
    setAssessmentId(initialAssessmentId || assessments[0]?.assessment_id || '');
    setFile(null);
    setNote('');
    setError('');
  }, [isOpen, assessments, initialAssessmentId]);

  if (!isOpen) return null;

  const chooseFile = selected => {
    setError('');
    if (!selected) return setFile(null);
    if (!ALLOWED_TYPES.includes(selected.type)) {
      setFile(null);
      setError('Upload a PDF, JPG or PNG scan.');
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setError('The scanned script must not exceed 5 MB.');
      return;
    }
    setFile(selected);
  };

  const submit = async event => {
    event.preventDefault();
    if (!assessmentId || !file) return setError('Select a marked CAM and attach its scanned script.');
    setUploading(true);
    setError('');
    try {
      await onSubmit({ assessmentId, file, note });
      onClose();
    } catch (submissionError) {
      setError(submissionError?.message || 'The marked script could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1050] grid place-items-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <form onSubmit={submit} className="w-full max-w-xl overflow-hidden rounded-xl border bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Upload marked CAM script</h2>
            <p className="mt-0.5 text-xs text-slate-500">Only CAMs with saved marks are available.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>

        <div className="space-y-4 p-5">
          <label className="block text-sm font-semibold text-slate-700">
            Marked CAM
            <select required value={assessmentId} onChange={event => setAssessmentId(event.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
              <option value="">Select marked CAM</option>
              {assessments.map(item => (
                <option key={item.assessment_id} value={item.assessment_id}>
                  {item.unit_code} · CAM {item.assessment_number} · {item.raw_marks} marks
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-semibold text-slate-700">
            Scanned marked script
            <span className="mt-1.5 flex min-h-28 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-5 py-4 text-center hover:border-primary/60 hover:bg-primary/5">
              <FileUp className="h-7 w-7 text-slate-400" />
              <span className="mt-2 text-sm font-medium text-slate-700">{file?.name || 'Choose PDF, JPG or PNG'}</span>
              <span className="mt-1 text-xs text-slate-500">Maximum file size: 5 MB</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="sr-only" onChange={event => chooseFile(event.target.files?.[0])} />
            </span>
          </label>

          <label className="block text-sm font-semibold text-slate-700">
            Note <span className="font-normal text-slate-400">(optional)</span>
            <textarea value={note} onChange={event => setNote(event.target.value)} rows="3" placeholder="Add a short note about this script" className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
          </label>

          {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t bg-slate-50 px-5 py-3">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={uploading || !assessmentId || !file}>{uploading ? 'Uploading…' : 'Submit evidence'}</Button>
        </div>
      </form>
    </div>
  );
};

export default POEUploadModal;
